<template>
  <div class="filter-editor" :class="accentClass">
    <div class="filter-list">
      <section class="filter-group">
        <article
          v-for="config in primaryFilters"
          :key="`${config.mode}-${config.id}`"
          :ref="cardRefFor(config)"
          class="filter-condition"
        >
          <label class="condition-label">{{ config.text }}</label>
          <div class="condition-input">
            <component
              :is="config.filter.UI"
              :key="editorSessionKey"
              :ref="filterRefFor(config)"
              :mode="config.mode"
              :types="types"
              :site-id="siteId"
            />
          </div>
          <LISAUIButton
            class="negated-control"
            size="lg"
            :variant="buttonVariant"
            :outlined="!stateFor(config).negated"
            icon="fas fa-not-equal"
            :aria-pressed="stateFor(config).negated"
            :aria-label="t('components.eventAction.FilterList.setFilterNegated')"
            :tooltip="t('components.eventAction.FilterList.setFilterNegated')"
            @click="stateFor(config).negated = !stateFor(config).negated"
          />
        </article>
      </section>

      <details
        v-if="collapsedFilters.length > 0"
        ref="additionalFilters"
        class="additional-filters"
      >
        <summary>
          <span>{{ t("global.joinList.more") }}</span>
          <i class="fas fa-chevron-down" aria-hidden="true" />
        </summary>
        <section class="filter-group">
          <article
            v-for="config in collapsedFilters"
            :key="`${config.mode}-${config.id}`"
            :ref="cardRefFor(config)"
            class="filter-condition"
          >
            <label class="condition-label">{{ config.text }}</label>
            <div class="condition-input">
              <component
                :is="config.filter.UI"
                :key="editorSessionKey"
                :ref="filterRefFor(config)"
                :mode="config.mode"
                :types="types"
                :site-id="siteId"
              />
            </div>
            <LISAUIButton
              class="negated-control"
              size="lg"
              :variant="buttonVariant"
              :outlined="!stateFor(config).negated"
              icon="fas fa-not-equal"
              :aria-pressed="stateFor(config).negated"
              :aria-label="t('components.eventAction.FilterList.setFilterNegated')"
              :tooltip="t('components.eventAction.FilterList.setFilterNegated')"
              @click="stateFor(config).negated = !stateFor(config).negated"
            />
          </article>
        </section>
      </details>
    </div>

    <footer class="editor-footer">
      <div class="logic-options">
        <span>Verknüpfung</span>
        <label>
          <input v-model="selectedFilterType" type="radio" :value="EABFilterType.AND" />
          Alle Bedingungen (AND)
        </label>
        <label>
          <input v-model="selectedFilterType" type="radio" :value="EABFilterType.OR" />
          Mindestens eine (OR)
        </label>
      </div>
      <div class="footer-actions">
        <LISAUIButton size="sm" variant="neutral" outlined @click="$emit('close')">
          {{ t("global.cancel") }}
        </LISAUIButton>
        <LISAUIButton size="sm" :variant="buttonVariant" class="apply-button" @click="applyFilters">
          Filter anwenden
        </LISAUIButton>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import {
  computed,
  nextTick,
  reactive,
  ref,
  type ComponentPublicInstance,
  type PropType,
} from "vue";
import { LISAButton as LISAUIButton, type LISAVariant } from "@fi-solutions/lisa-ui";
import { EABFilterType } from "@/graphql";
import { Filter, type FilterConfig, type FilterEventActionTypes } from "./FilterTypes";

type FilterUIInstance = {
  getFilter(): Filter | Filter[] | null | undefined;
  applyFilter(filter: Filter): void;
};

const props = defineProps({
  filterConfig: {
    required: true,
    type: Array as PropType<FilterConfig[]>,
  },
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    default: () => [],
  },
  title: {
    type: String,
    required: true,
  },
  accent: {
    type: String as PropType<"event" | "action">,
    required: true,
  },
  eventFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const emit = defineEmits<{
  apply: [filters: Filter[], type: EABFilterType];
  close: [];
}>();

const filterInstances = new Map<FilterConfig, FilterUIInstance>();
const cardElements = new Map<FilterConfig, HTMLElement>();
const conditionState = reactive(
  new Map<FilterConfig, { negated: boolean }>(
    props.filterConfig.map((config) => [config, { negated: false }]),
  ),
);
const selectedFilterType = ref(EABFilterType.AND);
const editorSessionKey = ref(0);
const additionalFilters = ref<HTMLDetailsElement>();
const accentClass = computed(() => `${props.accent}-editor`);
const buttonVariant = computed<LISAVariant>(() =>
  props.accent === "event" ? "success" : "warning",
);
const primaryFilters = computed(() => props.filterConfig.filter((config) => !config.collapsed));
const collapsedFilters = computed(() => props.filterConfig.filter((config) => config.collapsed));

function stateFor(config: FilterConfig) {
  let state = conditionState.get(config);
  if (state == null) {
    state = reactive({ negated: false });
    conditionState.set(config, state);
  }
  return state;
}

function setFilterRef(config: FilterConfig, instance: unknown) {
  if (instance == null) {
    filterInstances.delete(config);
    return;
  }
  filterInstances.set(config, instance as FilterUIInstance);
}

function filterRefFor(config: FilterConfig) {
  return (instance: unknown) => setFilterRef(config, instance);
}

function setCardRef(config: FilterConfig, element: Element | ComponentPublicInstance | null) {
  if (element instanceof HTMLElement) {
    cardElements.set(config, element);
  } else {
    cardElements.delete(config);
  }
}

function cardRefFor(config: FilterConfig) {
  return (element: Element | ComponentPublicInstance | null) => setCardRef(config, element);
}

function configFor(filter: Filter) {
  return props.filterConfig.find((config) => filter instanceof config.filter);
}

async function initialize(filters: Filter[], type: EABFilterType, focusFilter?: Filter) {
  conditionState.forEach((state) => {
    state.negated = false;
  });
  selectedFilterType.value = type;
  editorSessionKey.value += 1;
  await nextTick();
  if (additionalFilters.value != null) {
    additionalFilters.value.open = false;
  }
  filters.forEach((filter) => {
    const config = configFor(filter);
    if (config == null) {
      return;
    }
    stateFor(config).negated = filter.isNegated ?? false;
  });
  filters.forEach((filter) => {
    const config = configFor(filter);
    if (config == null) {
      return;
    }
    filterInstances.get(config)?.applyFilter(filter);
  });
  if (focusFilter != null) {
    const config = configFor(focusFilter);
    if (config != null) {
      if (config.collapsed && additionalFilters.value != null) {
        additionalFilters.value.open = true;
      }
      await nextTick();
      cardElements.get(config)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
}

function reset() {
  conditionState.forEach((state) => {
    state.negated = false;
  });
  selectedFilterType.value = EABFilterType.AND;
  editorSessionKey.value += 1;
}

function collectFilters() {
  return props.filterConfig.flatMap((config) => {
    const state = stateFor(config);
    const filter = filterInstances.get(config)?.getFilter();
    if (filter == null) {
      return [];
    }
    const filters = Array.isArray(filter) ? filter : [filter];
    filters.forEach((entry) => {
      entry.isNegated = state.negated;
    });
    return filters;
  });
}

function applyFilters() {
  const filters = collectFilters();
  emit("apply", filters, selectedFilterType.value);
}

defineExpose({
  initialize,
  reset,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.filter-editor {
  --filter-accent: var(--p-green-600);
  --filter-accent-hover: var(--p-green-700);
  --filter-accent-soft: var(--p-green-50);
  --p-primary-color: var(--filter-accent);
  --p-primary-hover-color: var(--filter-accent-hover);
  --p-primary-active-color: var(--p-green-800);
  --p-focus-ring-color: var(--p-green-200);

  display: flex;
  max-height: min(720px, var(--filter-popup-max-height, calc(100dvh - 12rem)));
  flex-direction: column;
  overflow: hidden;
  background: var(--p-surface-0);
}

.action-editor {
  --filter-accent: var(--p-orange-600);
  --filter-accent-hover: var(--p-orange-700);
  --filter-accent-soft: var(--p-orange-50);
  --p-primary-active-color: var(--p-orange-800);
  --p-focus-ring-color: var(--p-orange-200);
}

.editor-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1rem;
  border-color: var(--p-gray-200);
}

.filter-list {
  min-height: 0;
  flex: 1;
  overscroll-behavior: contain;
  overflow-x: hidden;
  overflow-y: auto;
}

.filter-group {
  padding: 0.65rem 1rem;
}

.filter-condition {
  display: grid;
  min-width: 0;
  align-items: center;
  grid-template-columns: minmax(12rem, 17rem) minmax(0, 1fr) 3.5rem;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid var(--p-gray-100);
}

.condition-label {
  font-weight: 600;
}

.logic-options,
.logic-options label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.logic-options label {
  cursor: pointer;
}

.condition-input {
  width: 100%;
  min-width: 0;
}

.negated-control {
  width: 3.5rem;
  min-width: 3.5rem;
  height: 3.5rem;
  min-height: 3.5rem;
}

.additional-filters {
  border-top: 1px solid var(--p-gray-200);
}

.additional-filters summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  color: var(--filter-accent);
  cursor: pointer;
  font-weight: 700;
  list-style: none;
}

.additional-filters[open] summary i {
  transform: rotate(180deg);
}

.editor-footer {
  gap: 0.75rem 1rem;
  border-top: 1px solid;
}

.footer-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.apply-button {
  min-width: 10rem;
  border-color: var(--filter-accent) !important;
  background: var(--filter-accent) !important;
}

:deep(.p-inputtext),
:deep(.p-select),
:deep(.p-multiselect),
:deep(.p-inputnumber),
:deep(.p-inputnumber-input),
:deep(.dp__main) {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

:deep(.p-inputtext:focus),
:deep(.p-select.p-focus),
:deep(.p-multiselect.p-focus),
:deep(.p-inputnumber-input:focus) {
  border-color: var(--filter-accent) !important;
  box-shadow: 0 0 0 1px var(--p-focus-ring-color) !important;
}

@media (max-width: 760px) {
  .filter-condition {
    grid-template-columns: minmax(0, 1fr) 3.5rem;
  }

  .condition-label {
    grid-column: 1 / -1;
  }
}
</style>
